import React, { useMemo, useState } from 'react';
import { Loader2, RefreshCw, Search, X } from 'lucide-react';

const labels = {
  order_confirmation: 'Confirmation de commande',
  tracking: 'Suivi d’expédition',
  cart_reminder: 'Relance panier',
  welcome: 'Bienvenue',
  recovery: 'Panier récupérable',
  sender_test: 'Test d’expéditeur',
};

const statusLabels = {
  accepted: 'Accepté par SendGrid',
  failed: 'Échec d’envoi',
};

const sourceLabels = {
  automatic: 'Automatique',
  manual: 'Relancé manuellement',
  archive_import: 'Historique importé',
};

export const EmailLogPanel = ({ emails, loading, resendingId, onResend }) => {
  const [query, setQuery] = useState('');
  const [type, setType] = useState('all');
  const [status, setStatus] = useState('all');
  const [source, setSource] = useState('all');
  const filteredEmails = useMemo(() => {
    const text = query.trim().toLowerCase();
    return emails.filter((email) => (
      (type === 'all' || email.template_type === type)
      && (status === 'all' || email.status === status)
      && (source === 'all' || email.source === source)
      && (!text || `${email.recipient} ${email.subject}`.toLowerCase().includes(text))
    ));
  }, [emails, query, source, status, type]);
  const clearFilters = () => {
    setQuery(''); setType('all'); setStatus('all'); setSource('all');
  };

  if (loading) {
    return <div className="py-10 flex items-center gap-2 text-black/50" data-testid="email-log-loading"><Loader2 className="w-5 h-5 animate-spin" /> Chargement…</div>;
  }

  return (
    <section className="space-y-4" data-testid="email-log-panel">
      <div className="bg-white p-5 border-l-4 border-[#2b3a26] flex items-center justify-between gap-4">
        <div>
          <p className="font-cond uppercase tracking-wide text-xs text-black/50">Journal d’envoi</p>
          <h2 className="font-display text-2xl" data-testid="email-log-count">{emails.length} email{emails.length > 1 ? 's' : ''} enregistré{emails.length > 1 ? 's' : ''}</h2>
        </div>
        <p className="text-xs text-black/50 max-w-xs text-right" data-testid="email-log-status-note">Accepté confirme la prise en charge par SendGrid.</p>
      </div>

      <div className="bg-white p-4 flex flex-wrap items-end gap-3" data-testid="email-log-filters">
        <label className="flex-1 min-w-[200px]" data-testid="email-filter-search-label">
          <span className="font-cond uppercase text-xs text-black/50 block mb-1">Destinataire ou objet</span>
          <span className="flex items-center border border-black/20 h-10 px-3 gap-2">
            <Search className="w-4 h-4 text-black/45" />
            <input value={query} onChange={(event) => setQuery(event.target.value)} data-testid="email-filter-search" placeholder="Rechercher…" className="w-full text-sm outline-none" />
          </span>
        </label>
        <label data-testid="email-filter-type-label">
          <span className="font-cond uppercase text-xs text-black/50 block mb-1">Type</span>
          <select value={type} onChange={(event) => setType(event.target.value)} data-testid="email-filter-type" className="h-10 border border-black/20 bg-white px-3 text-sm">
            <option value="all">Tous</option>
            {Object.entries(labels).map(([key, value]) => <option key={key} value={key}>{value}</option>)}
          </select>
        </label>
        <label data-testid="email-filter-status-label">
          <span className="font-cond uppercase text-xs text-black/50 block mb-1">Statut</span>
          <select value={status} onChange={(event) => setStatus(event.target.value)} data-testid="email-filter-status" className="h-10 border border-black/20 bg-white px-3 text-sm">
            <option value="all">Tous</option>
            {Object.entries(statusLabels).map(([key, value]) => <option key={key} value={key}>{value}</option>)}
          </select>
        </label>
        <label data-testid="email-filter-source-label">
          <span className="font-cond uppercase text-xs text-black/50 block mb-1">Origine</span>
          <select value={source} onChange={(event) => setSource(event.target.value)} data-testid="email-filter-source" className="h-10 border border-black/20 bg-white px-3 text-sm">
            <option value="all">Toutes</option>
            {Object.entries(sourceLabels).map(([key, value]) => <option key={key} value={key}>{value}</option>)}
          </select>
        </label>
        <button type="button" onClick={clearFilters} data-testid="email-filter-reset" className="h-10 w-10 flex items-center justify-center border border-black/20 hover:bg-black hover:text-white transition-colors" aria-label="Effacer les filtres">
          <X className="w-4 h-4" />
        </button>
        <p className="w-full text-xs text-black/50" data-testid="email-filter-result-count">{filteredEmails.length} résultat{filteredEmails.length > 1 ? 's' : ''}</p>
      </div>

      <div className="bg-white" data-testid="email-log-list">
        {filteredEmails.length === 0 ? (
          <p className="p-8 text-center text-black/50" data-testid="email-log-empty">Les prochains emails envoyés par la boutique apparaîtront ici.</p>
        ) : (
          <div className="divide-y">
            {filteredEmails.map((email) => {
              const isResending = resendingId === email.id;
              return (
                <article key={email.id} className="p-5 flex flex-wrap items-start justify-between gap-4" data-testid={`email-log-row-${email.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap gap-x-3 gap-y-1 items-center">
                      <p className="font-cond uppercase tracking-wide text-sm" data-testid={`email-log-type-${email.id}`}>{labels[email.template_type] || email.template_type}</p>
                      <span className={`text-[11px] px-2 py-0.5 uppercase font-semibold ${email.status === 'accepted' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`} data-testid={`email-log-status-${email.id}`}>
                        {statusLabels[email.status] || email.status}
                      </span>
                      <span className="text-[11px] text-black/45 uppercase" data-testid={`email-log-source-${email.id}`}>{sourceLabels[email.source] || email.source}</span>
                    </div>
                    <p className="text-sm mt-2 break-all" data-testid={`email-log-recipient-${email.id}`}>À : {email.recipient}</p>
                    <p className="text-sm text-black/65 mt-1 break-words" data-testid={`email-log-subject-${email.id}`}>{email.subject}</p>
                    <p className="text-xs text-black/45 mt-2" data-testid={`email-log-sent-at-${email.id}`}>{new Date(email.created_at).toLocaleString('fr-FR')}</p>
                    {email.error ? <p className="text-xs text-red-700 mt-2" data-testid={`email-log-error-${email.id}`}>{email.error}</p> : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => onResend(email.id)}
                    disabled={Boolean(resendingId)}
                    data-testid={`email-log-resend-${email.id}`}
                    className="h-9 px-3 inline-flex items-center gap-2 bg-[#2b3a26] text-white font-cond uppercase text-xs hover:bg-black transition-colors disabled:opacity-45"
                  >
                    {isResending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    {isResending ? 'Relance…' : 'Relancer'}
                  </button>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};